import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChatConversation } from "@/components/ui/chat-bubble";
import { format } from "date-fns";
import type { InterruptInfo } from "@/components/ui/chat-bubble";
import { filterTrackingPhrases, isOnlyTrackingPhrases } from "@/utils/transcriptFiltering";

interface Message {
  role: string;
  message: string;
  timestamp?: string;
  time?: number;
  endTime?: number;
  secondsFromStart?: number;
  interrupt?: InterruptInfo;
  // For nested format
  messages?: Array<{
    role: string;
    message: string;
    time?: number;
    endTime?: number;
    secondsFromStart?: number;
    interrupt?: InterruptInfo;
  }>;
  stats?: {
    duration: number;
  };
}

interface GrammarAnalysis {
  overall_score: number;
  confidence: number;
  analysis_timestamp: string;
  error_categories: {
    syntax_errors: number;
    punctuation_errors: number;
    word_choice_issues: number;
    sentence_structure: number;
    verb_tense_consistency: number;
  };
  detailed_feedback: Array<{
    category: string;
    message: string;
    suggestion: string;
  }>;
  user_speech_duration: number;
  analyzed_word_count: number;
  fluency_indicators: {
    sentence_complexity: string;
    vocabulary_diversity: string;
  };
  analysis_metadata?: {
    error?: string;
    model_used?: string;
  };
}

interface TranscriptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transcript: Message[] | null;
  summary?: string | null;
  grammarAnalysis?: GrammarAnalysis | null;
  callDate?: string;
  callInfo?: {
    phoneNumber?: string;
    direction?: string;
    duration?: number;
  };
}

const TranscriptDialog = ({
  open,
  onOpenChange,
  transcript,
  summary,
  grammarAnalysis,
  callDate,
  callInfo,
}: TranscriptDialogProps) => {
  // Don't prevent dialog from opening - let it show with fallback message if needed
  // if (!transcript || transcript.length === 0 || transcript.every(msg => msg === null || !msg)) {
  //   return null;
  // }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-4xl h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle>Call Transcript</DialogTitle>
          {callDate && (
            <DialogDescription>
              {format(new Date(callDate), "MMMM d, yyyy h:mm a")}
              {callInfo && (
                <span className="ml-2">
                  {callInfo.phoneNumber && (
                    <span className="mr-2">• {callInfo.phoneNumber}</span>
                  )}
                  {callInfo.direction && (
                    <span className="mr-2">• {callInfo.direction}</span>
                  )}
                  {callInfo.duration !== undefined && (
                    <span>• {formatDuration(callInfo.duration)} duration</span>
                  )}
                </span>
              )}
            </DialogDescription>
          )}
        </DialogHeader>

        <ScrollArea className="flex-1 border-t">
          <div className="p-6 space-y-4">
            {/* Display summary if available */}
            {summary && (
              <div className="mb-8 border border-border rounded-lg p-4 bg-muted/50 dark:bg-muted/20">
                <h3 className="font-medium mb-2 text-sm uppercase tracking-wide text-foreground">
                  Call Summary
                </h3>
                <div className="text-sm whitespace-pre-wrap text-foreground">{summary}</div>
              </div>
            )}

            {/* Display grammar analysis if available */}
            {grammarAnalysis && (
              <div className="mb-8 border border-border rounded-lg p-4 bg-muted/50 dark:bg-muted/20">
                <h3 className="font-medium mb-4 text-sm uppercase tracking-wide text-foreground">
                  Grammar Analysis
                </h3>
                
                {/* Overall Score */}
                <div className="mb-4 flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">Overall Score:</span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        grammarAnalysis.overall_score >= 80
                          ? "bg-good-bg text-good dark:bg-good-bg/30 dark:text-good"
                          : grammarAnalysis.overall_score >= 60
                          ? "bg-mid-bg text-mid dark:bg-mid-bg/30 dark:text-mid"
                          : "bg-danger-bg text-danger dark:bg-danger-bg/30 dark:text-danger"
                      }`}
                    >
                      {grammarAnalysis.overall_score}/100
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Confidence:</span>
                    <span className="text-sm font-medium">
                      {Math.round(grammarAnalysis.confidence * 100)}%
                    </span>
                  </div>
                </div>

                {/* Error Categories */}
                {Object.entries(grammarAnalysis.error_categories).some(([_, count]) => count > 0) && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium mb-2">Error Categories:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      {Object.entries(grammarAnalysis.error_categories).map(([category, count]) => (
                        count > 0 && (
                          <div key={category} className="flex justify-between">
                            <span className="text-muted-foreground">
                              {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                            </span>
                            <span className="font-medium">{count}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {/* Fluency Indicators */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-2">Fluency Indicators:</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sentence Complexity:</span>
                      <span className="font-medium capitalize">
                        {grammarAnalysis.fluency_indicators.sentence_complexity}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Vocabulary Diversity:</span>
                      <span className="font-medium capitalize">
                        {grammarAnalysis.fluency_indicators.vocabulary_diversity}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Analysis Statistics */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-2">Analysis Details:</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Words Analyzed:</span>
                      <span className="font-medium">{grammarAnalysis.analyzed_word_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Speech Duration:</span>
                      <span className="font-medium">
                        {Math.round(grammarAnalysis.user_speech_duration)}s
                      </span>
                    </div>
                  </div>
                </div>

                {/* Detailed Feedback */}
                {grammarAnalysis.detailed_feedback.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Detailed Feedback:</h4>
                    <div className="space-y-3">
                      {grammarAnalysis.detailed_feedback.map((feedback, index) => (
                        <div key={index} className="p-3 bg-background/50 rounded border">
                          <div className="flex items-start space-x-2">
                            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded font-medium capitalize">
                              {feedback.category.replace(/_/g, ' ')}
                            </span>
                            <div className="flex-1 text-sm">
                              <p className="mb-1">{feedback.message}</p>
                              {feedback.suggestion && (
                                <p className="text-muted-foreground italic">
                                  💡 {feedback.suggestion}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analysis Error */}
                {grammarAnalysis.analysis_metadata?.error && (
                  <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded">
                    <p className="text-sm text-destructive">
                      ⚠️ Analysis Error: {grammarAnalysis.analysis_metadata.error}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Transcript messages */}
            {(() => {
              if (!transcript || transcript.length === 0) {
                return (
                  <div className="text-center text-muted-foreground py-8">
                    No transcript available for this call.
                  </div>
                );
              }
              
              // Extract messages from the transcript
              const allMessages = [];
              
              try {
                transcript.forEach((transcriptItem) => {
                  // Handle nested structure (array of objects with messages property)
                  if (transcriptItem && transcriptItem.messages && Array.isArray(transcriptItem.messages)) {
                    transcriptItem.messages.forEach((msg) => {
                      if (msg && msg.role && msg.message) {
                        // Skip messages that contain only tracking phrases
                        if (msg.role === 'assistant' && isOnlyTrackingPhrases(msg.message)) {
                          return;
                        }
                        
                        allMessages.push({
                          role: msg.role,
                          message: msg.message,
                          timestamp: msg.time, // Pass the raw timestamp to ChatBubble for handling
                          time: msg.time,
                          endTime: msg.endTime,
                          secondsFromStart: msg.secondsFromStart,
                          interrupt: msg.interrupt
                        });
                      }
                    });
                  }
                  // Handle direct message format (array of message objects)
                  else if (transcriptItem && transcriptItem.role && transcriptItem.message) {
                    // Skip messages that contain only tracking phrases
                    if (transcriptItem.role === 'assistant' && isOnlyTrackingPhrases(transcriptItem.message)) {
                      return;
                    }
                    
                    allMessages.push({
                      role: transcriptItem.role,
                      message: transcriptItem.message,
                      timestamp: transcriptItem.timestamp || transcriptItem.time,
                      time: transcriptItem.time,
                      endTime: transcriptItem.endTime,
                      secondsFromStart: transcriptItem.secondsFromStart,
                      interrupt: transcriptItem.interrupt
                    });
                  }
                });
                
                return allMessages.length > 0 ? (
                  <ChatConversation messages={allMessages} />
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    <div>No conversation messages found in this call.</div>
                    {transcript[0]?.stats && (
                      <div className="text-xs mt-2 text-muted-foreground/70">
                        Call duration: {Math.round(transcript[0].stats.duration / 1000)}s
                      </div>
                    )}
                  </div>
                );
              } catch (error) {
                console.error("Error processing transcript:", error);
                return (
                  <div className="text-center text-muted-foreground py-8">
                    <div>Error processing transcript data.</div>
                    <div className="text-xs mt-2">Please check console for details.</div>
                  </div>
                );
              }
            })()}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TranscriptDialog;
